import { CurrencyCode, Money } from '@rematter/frontend-lib-api-client';
import { atom } from 'jotai';

import { balancesQueryAtom } from '../_graphql/queryFinanceBalance';
import { SortField, SortDirection } from '../types';

import { atomFromQueryString } from '~/shared/state';

// 🟣🟣🟣 PARAMS
export const $searchTerm = atomFromQueryString.string('q');
export const $currency = atomFromQueryString.string<CurrencyCode>('c');
export const $limit = atomFromQueryString.number('limit', 25);
export const $offset = atomFromQueryString.number('offset', 0);

// 🔘🔘🔘 PLAIN ATOMS
export const $currentSorting = atom<SortField>('name');
export const $currentSortDirection = atom<SortDirection>('ascending');

// 🟢🟢🟢 COMPUTED DATA -> SORTED AND FILTERED ACCOUNTS
export const $sortedAndFilteredAccounts = atom((get) => {
  const data = get($remoteBalances)?.data;
  const sortField = get($currentSorting);
  const sortDirection = get($currentSortDirection);

  if (!data) return [];

  return [...data].sort((a, b) => {
    if (sortField === 'name') {
      return sortDirection === 'ascending'
        ? a.name.localeCompare(b.name)
        : b.name.localeCompare(a.name);
    }

    if (sortField === 'balances') {
      const aTotal = a.balances.reduce(
        (sum: number, bal: Money) => sum + bal.amount,
        0,
      );
      const bTotal = b.balances.reduce(
        (sum: number, bal: Money) => sum + bal.amount,
        0,
      );
      return sortDirection === 'ascending' ? aTotal - bTotal : bTotal - aTotal;
    }

    if (sortField === 'pendingTransactions') {
      return sortDirection === 'ascending'
        ? a.pendingTransactions - b.pendingTransactions
        : b.pendingTransactions - a.pendingTransactions;
    }

    return 0;
  });
});

// 🟢🟢🟢 COMPUTED DATA -> PAGINATED ACCOUNTS
export const $paginatedAccounts = atom((get) => {
  const accounts = get($sortedAndFilteredAccounts);
  const offset = get($offset);
  const limit = get($limit);
  return accounts.slice(offset, offset + limit);
});

// 🟢🟢🟢 REMOTE DATA
export const $remoteBalances = balancesQueryAtom((get) => {
  const searchTerm = get($searchTerm);
  const currency = get($currency);

  return {
    variables: {
      searchTerm,
      currency,
    },
  };
});
